import React from "react";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";

import { useUserContext, useUserContextSetters } from "contexts/UserContext";

const BLOCK_FALLBACK = "Nenhum bloco";
const PAVEMENT_FALLBACK = "Nenhum pavimento";
const ENVIRONMENT_FALLBACK = "Nenhum ambiente";
const EMPTY_SELECTION_VALUE = "";

export const DetailedSelector = ({
  hideEnvironment = false,
}: DetailedSelectorProps) => {
  const {
    availableBlocks,
    currentBlock,
    availablePavements,
    currentPavement,
    availableEnvironments,
    currentEnvironment,
  } = useUserContext();
  const { setCurrentBlock, setCurrentPavement, setCurrentEnvironment } =
    useUserContextSetters();

  const selectedBlockId = currentBlock?.id || EMPTY_SELECTION_VALUE;
  const selectedPavementId = currentPavement?.id || EMPTY_SELECTION_VALUE;
  const selectedEnvironmentId = currentEnvironment?.id || EMPTY_SELECTION_VALUE;

  const handleBlockChange = (event: SelectChangeEvent<string>) => {
    const selectedBlock = availableBlocks.find(
      (block) => block.id === event.target.value
    );

    setCurrentBlock(selectedBlock);
    if (availablePavements.length > 0) {
      setCurrentPavement(undefined);
      setCurrentEnvironment(undefined);
    }
  };

  const handlePavementChange = (event: SelectChangeEvent<string>) => {
    const selectedPavement = availablePavements.find(
      (pavement) => pavement.id === event.target.value
    );

    setCurrentPavement(selectedPavement);
    if (availableEnvironments.length > 0) {
      setCurrentEnvironment(undefined);
    }
  };

  const handleEnvironmentChange = (event: SelectChangeEvent<string>) => {
    const selectedEnvironment = availableEnvironments.find(
      (environment) => environment.id === event.target.value
    );

    setCurrentEnvironment(selectedEnvironment);
  };

  return (
    <Grid
      container
      item
      direction="row"
      justifyContent="space-between"
      gap={0.5}
    >
      <Grid item container sx={{ flex: 1 }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel id="block-selector-label">Bloco</InputLabel>
          <Select
            displayEmpty
            labelId="block-selector-label"
            label="Bloco"
            value={selectedBlockId}
            onChange={handleBlockChange}
            renderValue={(value) => {
              if (value) {
                return (
                  availableBlocks.find((block) => block.id === value)?.name ||
                  ""
                );
              }
            }}
          >
            {availableBlocks.length === 0 ? (
              <MenuItem disabled value={EMPTY_SELECTION_VALUE}>
                <Box component="span">{BLOCK_FALLBACK}</Box>
              </MenuItem>
            ) : (
              availableBlocks.map((block) => (
                <MenuItem key={block.id} value={block.id}>
                  {block.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Grid>

      <Grid item container sx={{ flex: 1 }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel id="pavement-selector-label">Pavimento</InputLabel>
          <Select
            displayEmpty
            labelId="pavement-selector-label"
            label="Pavimento"
            value={selectedPavementId}
            onChange={handlePavementChange}
            renderValue={(value) => {
              if (value) {
                return (
                  availablePavements.find((pavement) => pavement.id === value)
                    ?.name || ""
                );
              }
            }}
          >
            {availablePavements.length === 0 ? (
              <MenuItem disabled value={EMPTY_SELECTION_VALUE}>
                <Box component="span">{PAVEMENT_FALLBACK}</Box>
              </MenuItem>
            ) : (
              availablePavements.map((pavement) => (
                <MenuItem key={pavement.id} value={pavement.id}>
                  {pavement.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Grid>

      {!hideEnvironment && (
        <Grid item container sx={{ flex: 1 }}>
          <FormControl sx={{ flex: 1 }}>
            <InputLabel id="environment-selector-label">Ambiente</InputLabel>
            <Select
              displayEmpty
              labelId="environment-selector-label"
              label="Ambiente"
              value={selectedEnvironmentId}
              onChange={handleEnvironmentChange}
              renderValue={(value) => {
                if (value) {
                  return (
                    availableEnvironments.find(
                      (environment) => environment.id === value
                    )?.name || ""
                  );
                }
              }}
            >
              {availableEnvironments.length === 0 ? (
                <MenuItem disabled value={EMPTY_SELECTION_VALUE}>
                  <Box component="span">{ENVIRONMENT_FALLBACK}</Box>
                </MenuItem>
              ) : (
                availableEnvironments.map((environment) => (
                  <MenuItem key={environment.id} value={environment.id}>
                    {environment.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
      )}
    </Grid>
  );
};

export type DetailedSelectorProps = {
  hideEnvironment?: boolean;
};
